import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SentryService } from '@app/services/sentry.service';
import { PotentialSavingsService } from '@modules/potential-save-modal/potential-savings.service';
import { Observable, Subject } from 'rxjs';
import { AnalyticsEvents, MixpanelService } from '@app/services/mixpanel.service';
import { Dict } from 'mixpanel-browser';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from './user/user.service';
import { Product, SuccessSupportModalComponent, SupportModalComponent } from '@app/support-request';
import { ConfigFacadeService } from '@app/core/services/config/config-facade.service';
import { PartnerAliasEnum } from '@partners/partner-alias.enum';
import { OrganizationsService } from './organizations.service';
import { switchMap, take } from 'rxjs/operators';
import { CrisisModalComponent } from '@modules/commitment-manager/modules/dashboard/crisis-modal/crisis-modal.component';
import {
	CRISIS_DIALOG_CONTAINER,
	CRISIS_MODAL_WIDTH,
} from '@modules/commitment-manager/modules/dashboard/crisis-modal/crisis-modal.model';
import { FEATURE_FLAGS } from '@app/infrastructure/feature-flag-manager/feature-flag-list';
import { FeatureFlagManagerService } from '@app/infrastructure/feature-flag-manager/feature-flag-manager.service';
import { IPartnerModalTypes } from '@modules/partner-portal/interfaces/partner-modals.interface';
import { ModalService } from '@app/services/modal.service';
import { PartnerAccount } from '@modules/partner-portal/interfaces/partner-accounts.interface';
import { PartnerLink } from '@modules/partner-portal/interfaces/partner-links.interface';
import { Organization } from '@app/interfaces/accounts_list';
import { SecurityService } from '@app/services/security.service';
import { LoaderService } from '@app/services/loader.service';
import { CoralogixService } from './coralogix.service';
import { NotificationSetModalController } from '@modules/notification-management/modals/notification-set-modal/notification-set-modal.controller';
import { EditNotificationSetModalController } from '@modules/notification-management/modals/edit-notification-set-modal/edit-notification-set-modal.controller';
import { IListNotificationsSet } from '@modules/notification-management/interfaces/notifications.interface';
import { PresenterModeService } from './presenter-mode.service';

export enum PostMessageOrigin {
	Remote = 'remote',
	Host = 'host',
}

// TODO moved to shared lib ZES-13110
export interface PostEvent {
	initiator: PostMessageOrigin;
	type: PostEventType;
	payload?: Record<string | AnalyticsEvents, unknown>;
}

// TODO moved to shared lib ZES-13110
export enum PostEventType {
	OpenPotentialSavingModal = 'open-potential-saving',
	OpenSupportModal = 'open-support-modal',
	Navigation = 'navigation',
	Fetch = 'fetch',
	CloseSentryTransactionFromOptHub = 'close-sentry-transaction-from-opt-hub',
	mixPanelTrack = 'mix-panel-track',
	OpenCrisisModal = 'open-temp-dialog',
	OpenPartnerPortalGenerateLinkModal = 'open-partner-portal-generate-link-modal',
	OpenPartnerPortalUpdatePermissionsModal = 'open-partner-portal-update-permissions-modal',
	OpenPartnerPortalDeleteLinkModal = 'open-partner-portal-delete-link-modal',
	OpenPartnerPortalOnboardLinkModal = 'open-partner-portal-onboard-link-modal',
	LogOut = 'logout',
	SetCurrentOrganization = 'set-current-organization',
	SetHasPotentialSavings = 'set-has-potential-savings',
	ToggleGlobalLoader = 'toggle-global-loader',
	UpdateApplicationContext = 'update-application-context',
	OpenNotificationCreateModal = 'open-notification-create-modal',
	OpenNotificationEditModal = 'open-notification-edit-modal',
	RefreshNotificationList = 'refresh-notification-list',
	TogglePresenterMode = 'toggle-presenter-mode',
}

@Injectable({
	providedIn: 'root',
})
export class PostMessageService {
	private messageSubject: Subject<PostEvent> = new Subject<PostEvent>();
	private eventHistory: PostEvent[] = [];

	constructor(
		private modalService: ModalService,
		private featureFlagManagerService: FeatureFlagManagerService,
		private router: Router,
		private potentialSavingsService: PotentialSavingsService,
		private sentryService: SentryService,
		private mixpanelService: MixpanelService,
		private dialog: MatDialog,
		private userService: UserService,
		private configFacadeService: ConfigFacadeService,
		private organizationsService: OrganizationsService,
		private securityService: SecurityService,
		private loaderService: LoaderService,
		private coralogixService: CoralogixService,
		private setModalController: NotificationSetModalController,
		private editModalController: EditNotificationSetModalController,
		private presenterModeService: PresenterModeService,
	) {}

	public init(): void {
		globalThis.addEventListener('message', (event: MessageEvent) => {
			if (event.data.initiator === PostMessageOrigin.Remote) {
				const receivedEvent = event.data as PostEvent;
				this.messageSubject.next(receivedEvent);
				if (receivedEvent.type === PostEventType.Navigation) {
					const destinationUrl = receivedEvent.payload.url as string;
					if (receivedEvent.payload?.config) {
						void this.router.navigate([destinationUrl], receivedEvent.payload.config);
					} else {
						void this.router.navigateByUrl(destinationUrl);
					}
				}
				if (receivedEvent.type === PostEventType.OpenPotentialSavingModal) {
					this.openPotentialSavingModal();
				}
				if (receivedEvent.type === PostEventType.TogglePresenterMode) {
					this.presenterModeService.setMode(event.data.payload.applyIncognito);
				}
				if (receivedEvent.type === PostEventType.OpenPartnerPortalGenerateLinkModal) {
					this.openPartnerPortalGenerateLinkModal(event.data.payload);
				}
				if (receivedEvent.type === PostEventType.OpenPartnerPortalUpdatePermissionsModal) {
					this.openPartnerPortalUpdatePermissionsModal(event.data.payload);
				}
				if (receivedEvent.type === PostEventType.OpenPartnerPortalOnboardLinkModal) {
					this.openPartnerPortalOnboardModal(event.data.payload);
				}
				if (receivedEvent.type === PostEventType.OpenPartnerPortalDeleteLinkModal) {
					this.openPartnerPortalDeleteModal(event.data.payload);
				}
				if (receivedEvent.type === PostEventType.OpenSupportModal) {
					this.openSupportFormModal(event.data.payload ?? null);
				}
				if (receivedEvent.type === PostEventType.CloseSentryTransactionFromOptHub) {
					this.sentryService.endSpan();
				}
				if (receivedEvent.type === PostEventType.mixPanelTrack) {
					const { eventName, trackingProps } = receivedEvent.payload as {
						eventName: string;
						trackingProps: Dict;
					};
					this.mixpanelService.track(eventName as AnalyticsEvents, trackingProps);
				}
				if (receivedEvent.type === PostEventType.OpenCrisisModal) {
					const enableCrisisPopover = this.featureFlagManagerService.flags[FEATURE_FLAGS.ENABLE_CRISIS_POPOVER];
					const isOptimizationHubEnabled = this.featureFlagManagerService.flags[FEATURE_FLAGS.ENABLE_OPTIMIZATION_HUB];
					if (enableCrisisPopover?.isActive && !isOptimizationHubEnabled) {
						this.dialog.open(CrisisModalComponent, {
							panelClass: CRISIS_DIALOG_CONTAINER,
							width: `${CRISIS_MODAL_WIDTH}px`,
						});
					}
				}
				if (receivedEvent.type === PostEventType.LogOut) {
					this.userService.logout();
				}
				if (receivedEvent.type === PostEventType.SetCurrentOrganization) {
					const { currentOrganization, isSupportedUser } = receivedEvent.payload as {
						currentOrganization: Organization;
						isSupportedUser: boolean;
					};
					this.loaderService.showLoader();
					window.zestyHost.currentOrganization = currentOrganization;
					window.zestyHost.isSupportedUser = isSupportedUser;
					this.loaderService.hideLoader();
				}
				if (receivedEvent.type === PostEventType.SetHasPotentialSavings) {
					const { hasPotentialSavings } = receivedEvent.payload as { hasPotentialSavings: boolean };
					this.securityService.hasPotentialSavings.next(hasPotentialSavings);
				}
				if (receivedEvent.type === PostEventType.ToggleGlobalLoader) {
					const { loading } = receivedEvent.payload as { loading: boolean };
					if (loading) {
						this.loaderService.showLoader();
					} else {
						this.loaderService.hideLoader();
					}
				}
				if (receivedEvent.type === PostEventType.UpdateApplicationContext) {
					const context = receivedEvent.payload as { application: string; version: string };
					this.coralogixService.updateAppContext(context);
				}
				if (receivedEvent.type === PostEventType.OpenNotificationCreateModal) {
					this.openNotificationCreateModal();
				}
				if (receivedEvent.type === PostEventType.OpenNotificationEditModal) {
					this.openNotificationEditModal(event.data.payload);
				}
			}
		});
	}

	public dispatchMessage(event: PostEvent): void {
		globalThis.postMessage(event, '*');
		this.eventHistory.push(event);
	}

	public subscribeToMessage(): Observable<PostEvent> {
		return this.messageSubject.asObservable();
	}

	public unsubscribe(): void {
		this.messageSubject.complete();
	}

	public getHistory(): PostEvent[] {
		return [...this.eventHistory];
	}

	private openPotentialSavingModal(): void {
		this.potentialSavingsService.openModal();
	}

	private openNotificationCreateModal(): void {
		this.setModalController.init(+this.userService.organizationId);
	}

	private openNotificationEditModal(payload?: IListNotificationsSet): void {
		if (payload.name) {
			this.editModalController.init({ org_id: +this.userService.organizationId, name: payload.name });
		}
	}

	private openPartnerPortalGenerateLinkModal(payload?: { resellerId: string; selectedOrganization: string }): void {
		this.modalService.openModal(IPartnerModalTypes.generateLinks, payload);
	}

	private openPartnerPortalUpdatePermissionsModal(payload?: { resellerId: string; row: PartnerAccount }): void {
		this.modalService.openModal(IPartnerModalTypes.reonoboardAccounts, payload);
	}

	private openPartnerPortalOnboardModal(payload?: { links: PartnerLink[]; isReonboard: boolean }): void {
		this.modalService.openModal(IPartnerModalTypes.onboardAccounts, payload);
	}

	private openPartnerPortalDeleteModal(payload?: { link: PartnerLink }): void {
		this.modalService.openModal(IPartnerModalTypes.confirmLinkDelete, payload);
	}

	private openSupportFormModal(payload?: { product: Product; message?: string }) {
		if (this.dialog.openDialogs.length == 0) {
			this.organizationsService
				.getCurrentUserOrganization()
				.pipe(
					take(1),
					switchMap(({ org_name }) =>
						this.dialog
							.open(SupportModalComponent, {
								width: '557px',
								height: 'fit-content',
								data: {
									userName: this.userService.userNickname,
									userEmail: this.userService.userEmail,
									orgName: this.isPartnerSupportForm ? `(${this.configFacadeService.partnerName})` : org_name,
									payload: payload ?? null,
								},
							})
							.afterClosed()
							.pipe(take(1)),
					),
				)
				.subscribe((isSubmitted) => {
					if (isSubmitted) {
						this.dialog.open(SuccessSupportModalComponent, {
							width: '431px',
							height: 'fit-content',
							data: {
								userEmail: this.userService.userEmail,
							},
						});
					}
				});
		}
	}
	private isPartnerSupportForm(): boolean {
		return !!this.configFacadeService.partnerId && this.configFacadeService.partnerPrefix !== PartnerAliasEnum.Zesty;
	}
}
